(() => {
  // If we already mounted the header scene controller (e.g. via view transitions),
  // just trigger a re-sync for the new page state.
  if (window.__hosBrandSystem && typeof window.__hosBrandSystem.resync === 'function') {
    window.__hosBrandSystem.resync();
    return;
  }
  window.__hosBrandSystem = window.__hosBrandSystem || {};

  const boot = () => {
    const body = document.body;

    const hashString = (str) => {
      // FNV-1a: stable variant selection without randomness/jitter.
      let h = 2166136261;
      for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619);
      }
      return h >>> 0;
    };

    const variantKeys = {
      heritage: ['firework', 'diya', 'petals'],
      enamel: ['showlights', 'meena', 'phone'],
      italian: ['cars', 'cuff', 'watch'],
    };

    const getVibeFromPage = () => {
      const cls = body.classList;
      const path = window.location.pathname;

      // Product pages already carry an edit palette signal via rc-px-*.
      if (
        cls.contains('rc-px-heritage') ||
        cls.contains('template-suffix-heritage-pilot') ||
        cls.contains('template-suffix-heritage-edit')
      )
        return 'heritage';
      if (
        cls.contains('rc-px-enamel') ||
        cls.contains('template-suffix-enamel-pilot') ||
        cls.contains('template-suffix-enamel-edit')
      )
        return 'enamel';
      if (
        cls.contains('rc-px-italian') ||
        cls.contains('template-suffix-italian-pilot') ||
        cls.contains('template-suffix-italian-edit')
      )
        return 'italian';

      // Collection pages route by URL.
      if (path.includes('/collections/heritage-edit')) return 'heritage';
      if (path.includes('/collections/enamel-edit')) return 'enamel';
      if (path.includes('/collections/italian-edit')) return 'italian';

      // Legacy / alternate headers: pilot classes.
      if (document.querySelector('.hos-pilot--heritage')) return 'heritage';
      if (document.querySelector('.hos-pilot--enamel')) return 'enamel';
      if (document.querySelector('.hos-pilot--italian')) return 'italian';

      return '';
    };

    const syncHeaderScene = () => {
      const vibe = getVibeFromPage();
      const path = window.location.pathname;

      const currentVibe = body.classList.contains('hos-vibe--heritage')
        ? 'heritage'
        : body.classList.contains('hos-vibe--enamel')
          ? 'enamel'
          : body.classList.contains('hos-vibe--italian')
            ? 'italian'
            : '';

      body.classList.add('hos-brand-page');
      if (currentVibe !== vibe) {
        body.classList.remove('hos-vibe--heritage', 'hos-vibe--enamel', 'hos-vibe--italian');
        if (vibe) body.classList.add(`hos-vibe--${vibe}`);
      }

      const logo = document.querySelector('.header-logo');
      if (!logo) return;

      const existing = logo.querySelector('.hos-shared-scene');
      if (!vibe) {
        existing?.remove?.();
        return;
      }

      const keys = variantKeys[vibe] || [];
      const seed = `${vibe}|${path}|${body.className}`;
      const variant = keys.length ? keys[hashString(seed) % keys.length] : '';

      const sceneClass = `hos-shared-scene hos-shared-scene--${vibe}${variant ? ` hos-scene--${variant}` : ''}`;
      let sceneHTML = '';
      let scene = existing;
      if (!scene) {
        scene = document.createElement('span');
        logo.prepend(scene);
      }

      if (vibe === 'heritage') {
        if (variant === 'diya') {
          sceneHTML = '<i class="hos-diya"></i>';
        } else if (variant === 'petals') {
          sceneHTML = '<i class="hos-petal hos-petal--one"></i><i class="hos-petal hos-petal--two"></i>';
        } else {
          sceneHTML = '<i class="hos-firework hos-firework--one"></i><i class="hos-firework hos-firework--two"></i>';
        }
      } else if (vibe === 'enamel') {
        if (variant === 'meena') {
          sceneHTML = '<i class="hos-meena-chip hos-chip--one"></i><i class="hos-meena-chip hos-chip--two"></i>';
        } else if (variant === 'phone') {
          sceneHTML = '<i class="hos-phone-glow"></i>';
        } else {
          sceneHTML =
            '<span class="hos-showlight hos-showlight--one"></span><span class="hos-showlight hos-showlight--two"></span><span class="hos-showlight hos-showlight--three"></span>';
        }
      } else if (vibe === 'italian') {
        if (variant === 'cuff') {
          sceneHTML = '<i class="hos-cuffline"></i>';
        } else if (variant === 'watch') {
          sceneHTML = '<i class="hos-watch-sweep"></i>';
        } else {
          sceneHTML =
            '<i class="hos-mini-car hos-mini-car--one"></i><i class="hos-mini-car hos-mini-car--two"></i><i class="hos-mini-car hos-mini-car--three"></i>';
        }
      }

      if (scene.className !== sceneClass) scene.className = sceneClass;
      if (scene.innerHTML !== sceneHTML) scene.innerHTML = sceneHTML;
    };

    // Expose to the top-level guard so future asset executions can resync immediately.
    window.__hosBrandSystem.resync = syncHeaderScene;

    syncHeaderScene();

    // rc-px-* class palette signals are applied a bit after initial render.
    // Keep the header scene synced whenever the body's class attribute changes.
    let classResyncTimer = null;
    const scheduleFromBodyClass = () => {
      clearTimeout(classResyncTimer);
      classResyncTimer = setTimeout(syncHeaderScene, 220);
    };
    const classObserver = new MutationObserver(scheduleFromBodyClass);
    classObserver.observe(body, { attributes: true, attributeFilter: ['class'] });
    scheduleFromBodyClass();

    const pageScroller = document.querySelector('.page-wrapper');
    const syncHeader = () => document.body.classList.toggle('hos-header-scrolled', (pageScroller?.scrollTop || window.scrollY) > 36);
    syncHeader();
    window.addEventListener('scroll', syncHeader, { passive:true });
    pageScroller?.addEventListener('scroll', syncHeader, { passive:true });

    /* A quiet scroll-linked orbit gives the three collection rails their
       own pulse without putting another shine over the jewellery. */
    const rails = [...document.querySelectorAll('.template-suffix-house-of-silver .rc-showcase')];
    let orbitFrame = 0;
    const syncOrbits = () => {
      orbitFrame = 0;
      const viewport = window.innerHeight || 1;
      rails.forEach((rail) => {
        const rect = rail.getBoundingClientRect();
        const progress = Math.max(-1, Math.min(1, (viewport * .5 - (rect.top + rect.height * .5)) / viewport));
        rail.style.setProperty('--hos-orbit-y', `${progress * 34}px`);
      });
    };
    const requestOrbit = () => {
      if (orbitFrame || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      orbitFrame = requestAnimationFrame(syncOrbits);
    };
    syncOrbits();
    window.addEventListener('scroll', requestOrbit, { passive:true });
    pageScroller?.addEventListener('scroll', requestOrbit, { passive:true });
    window.addEventListener('resize', requestOrbit, { passive:true });

    // View-transition swaps may not fully reload this script, so re-evaluate the vibe + scene.
    const scheduleResync = () => {
      syncHeaderScene();
      setTimeout(syncHeaderScene, 350);
    };
    window.addEventListener('pageswap', scheduleResync, { passive:true });
    window.addEventListener('pagereveal', scheduleResync, { passive:true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once:true });
  else boot();
})();
